import { useEffect, useState } from 'react';
import {
  discoverLocalServer,
  checkLocalServer,
  setCachedLocalServerUrl,
  getCachedLocalServerUrl,
  isSecureOrigin,
  isNativeLocalServerAvailable,
  nativeSavedLocalServer,
  nativeDiscoverLocalServer,
  nativeOpenLocalServer,
} from '../../utils/localServerDiscovery';

// Opens the full Parvekshak application (login, cameras, alerts — everything,
// not just the ad-hoc RTSP view) at its LAN address. Tries to find it
// automatically first; if that fails, asks for the IP directly rather than
// leaving the user stuck on a spinner.
//
// Inside the Android app, finding, checking and opening the box are all
// delegated to the app (see the native bridge in localServerDiscovery.js):
// it can scan the phone's real subnet, reach http:// from this https page,
// and remembers the box's IP + MAC. The WebView also refuses a plain link to
// a 192.168.x.x address while on the remote origin, which is why "Open" goes
// through the bridge rather than an <a href>.
//
// In a browser on a secure (https) origin — the installed PWA at
// app.parvekshak.in — none of the fetch-based checking can work at all: a
// browser blocks fetch() to plain http:// as mixed content, silently, with no
// workaround. So this skips straight to manual entry there. See
// isSecureOrigin() in localServerDiscovery.js.
//
// In the browser, opening the target is a real <a href> the user taps, not a
// script-driven window.location.href assignment — the manifest declares this
// app display:"standalone" with scope "/", and in that installed-app mode a
// script-triggered navigation to an out-of-scope origin gets silently
// swallowed on at least some Android/Chrome builds (confirmed: tapping
// "Connect" did nothing at all, no browser error, nothing). A genuine anchor
// click is honored as a real external-navigation gesture where the scripted
// assignment wasn't.
const NATIVE        = isNativeLocalServerAvailable();
const SECURE_ORIGIN = !NATIVE && isSecureOrigin();

function normalizeTarget(input) {
  const trimmed = input.trim();
  const url = /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
  return url.replace(/\/+$/, '').replace(/:8765$/, ''); // raw API port → the app on :80
}

const hostOf = (url) => (url || '').replace(/^https?:\/\//, '');

// Both paths resolve to { url, mac } or null.
async function discover() {
  if (NATIVE) return nativeDiscoverLocalServer();
  const url = await discoverLocalServer();
  return url ? { url, mac: '' } : null;
}

export default function LocalAppTab() {
  const [phase, setPhase]     = useState(SECURE_ORIGIN ? 'manual' : 'scanning');
  const [found, setFound]     = useState(null);   // { url, mac }
  const [ipInput, setIpInput] = useState(() => hostOf(getCachedLocalServerUrl()));
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');

  const runDiscovery = () => {
    let cancelled = false;
    setPhase('scanning');
    discover().then((s) => {
      if (cancelled) return;
      if (s) {
        setCachedLocalServerUrl(s.url);
        setFound(s);
        setPhase('found');
      } else {
        setPhase('manual');
      }
    });
    return () => { cancelled = true; };
  };

  useEffect(() => {
    if (SECURE_ORIGIN) return undefined;
    if (NATIVE) nativeSavedLocalServer().then((s) => { if (s) setIpInput(hostOf(s.url)); });
    return runDiscovery();
  }, []);

  const openNative = async (url) => {
    setBusy(true);
    setError('');
    const s = await nativeOpenLocalServer(url);
    // On success the app has already switched to the box.
    setBusy(false);
    if (!s) setError("Couldn't reach a Parvekshak device at that address.");
  };

  const handleManualConnect = async (e) => {
    e.preventDefault();
    if (!ipInput.trim()) return;

    if (NATIVE) { openNative(ipInput.trim()); return; }

    if (SECURE_ORIGIN) {
      // Can't pre-validate over https (see module comment) — just show the
      // real link to tap. If it's unreachable, the browser reports that once
      // the user actually navigates.
      const target = normalizeTarget(ipInput);
      setCachedLocalServerUrl(target);
      setFound({ url: target, mac: '' });
      setPhase('found');
      return;
    }

    setBusy(true);
    setError('');
    const ok = await checkLocalServer(ipInput.trim());
    setBusy(false);
    if (!ok) {
      setError("Couldn't reach a Parvekshak device at that address.");
      return;
    }
    setCachedLocalServerUrl(ok);
    setFound({ url: ok, mac: '' });
    setPhase('found');
  };

  const useDifferentIp = () => {
    setFound(null);
    setError('');
    setPhase('manual');
  };

  if (phase === 'scanning') {
    return (
      <div className="lm-panel lm-app-tab">
        <h2>Local App</h2>
        <p className="lm-empty">Looking for your Parvekshak device on this network…</p>
      </div>
    );
  }

  if (phase === 'found') {
    return (
      <div className="lm-panel lm-app-tab">
        <h2>Local App</h2>
        <p className="lm-device-line">Your Parvekshak device: <strong>{found.url}</strong></p>
        {found.mac && <p className="lm-empty">MAC {found.mac.toUpperCase()}</p>}
        <div className="lm-app-actions">
          {NATIVE ? (
            <button className="lm-btn" onClick={() => openNative(found.url)} disabled={busy}>
              {busy ? 'Connecting…' : 'Open Local App'}
            </button>
          ) : (
            <a className="lm-btn" href={found.url} target="_top" rel="noopener">Open Local App</a>
          )}
          <button className="lm-link-btn" onClick={useDifferentIp}>
            Not this device? Use a different IP
          </button>
        </div>
        <p className="lm-empty lm-app-hint">You'll sign in with your usual account on the next screen.</p>
        {error && <div className="lm-error">⚠️ {error}</div>}
      </div>
    );
  }

  return (
    <div className="lm-panel lm-app-tab">
      <h2>Local App</h2>
      <p>
        {SECURE_ORIGIN
          ? 'This page can’t check for your Parvekshak device automatically over a secure connection. Enter its address from your router’s device list.'
          : 'Couldn\'t find a Parvekshak device automatically. Enter its address from your router\'s device list.'}
      </p>
      <form className="lm-form" onSubmit={handleManualConnect}>
        <div className="lm-field">
          <label>Device IP</label>
          <input
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            placeholder="192.168.1.42"
            autoComplete="off"
            inputMode="decimal"
          />
        </div>
        {error && <div className="lm-error">⚠️ {error}</div>}
        <button className="lm-btn" type="submit" disabled={busy}>
          {busy ? 'Checking…' : 'Continue'}
        </button>
      </form>
      {!SECURE_ORIGIN && (
        <button className="lm-link-btn" style={{ marginTop: 10 }} onClick={runDiscovery}>
          Retry automatic discovery
        </button>
      )}
    </div>
  );
}
